import React, { useState } from 'react';
import { Button } from './ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from './ui/dialog';
import { MessageCircle, Copy, CheckCircle2 } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * WhatsApp Placeholder Component (Section 2 Fix)
 * Shows "coming soon" message with copy fallback before E5 WhatsApp Connect is built
 */
const WhatsAppPlaceholder = ({ 
  messageText, 
  buttonLabel = 'Send via WhatsApp',
  variant = 'outline',
  size = 'default',
  className = '',
  onCopy
}) => {
  const [dialogOpen, setDialogOpen] = useState(false);
  const [copied, setCopied] = useState(false);

  const handleButtonClick = () => {
    setDialogOpen(true);
  };

  const handleCopyToClipboard = async () => {
    try {
      await navigator.clipboard.writeText(messageText);
      setCopied(true);
      toast.success('Copied to clipboard!');
      
      // Log user intent for TAO tracking
      try {
        await axios.post(`${API}/observation/whatsapp-intent`, {
          feature: 'whatsapp_intent',
          user_action: 'copy_fallback',
          message_length: messageText.length
        });
      } catch (e) {
        // Non-critical, don't block user flow
        console.log('TAO log skipped');
      }

      if (onCopy) onCopy();
      
      // Reset copied state after 2 seconds
      setTimeout(() => setCopied(false), 2000);
    } catch (error) {
      toast.error('Failed to copy');
    }
  };

  return (
    <>
      <Button
        variant={variant}
        size={size}
        className={`${className}`}
        onClick={handleButtonClick}
        data-testid="whatsapp-send-btn"
      >
        <MessageCircle className="w-4 h-4 mr-2" />
        {buttonLabel}
      </Button>

      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="rounded-2xl max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <MessageCircle className="w-5 h-5 text-green-500" />
              WhatsApp Connect Coming Soon
            </DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4 pt-2">
            <p className="text-nurture-muted text-sm">
              Direct WhatsApp sending is coming in a future update. For now, you can copy this message and paste it manually.
            </p>
            
            {messageText && (
              <div className="p-4 bg-nurture-cream rounded-xl">
                <p className="text-nurture-navy text-sm whitespace-pre-line">
                  {messageText}
                </p>
              </div>
            )}
            
            <Button
              onClick={handleCopyToClipboard}
              className={`w-full rounded-xl ${
                copied 
                  ? 'bg-nurture-sage hover:bg-nurture-sage-hover' 
                  : 'bg-nurture-terracotta hover:bg-nurture-terracotta-hover'
              } text-white`}
              data-testid="copy-to-clipboard-btn"
            >
              {copied ? (
                <>
                  <CheckCircle2 className="w-4 h-4 mr-2" />
                  Copied!
                </>
              ) : (
                <>
                  <Copy className="w-4 h-4 mr-2" />
                  Copy to Clipboard
                </>
              )}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default WhatsAppPlaceholder;
